'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Bell, Check, Trophy, Users, Calendar, Star, Play, Target, Globe, Clock, MapPin, Zap } from 'lucide-react';

interface SportSubscriber {
  name: string;
  phone: string;
  email: string;
  sport: string;
  team: string;
  notifications: string[];
}

const scheduleMatches = [
  {
    id: 'm1',
    league: 'Premier League',
    leagueBadge: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    homeTeam: 'Arsenal',
    awayTeam: 'Chelsea',
    homeScore: null,
    awayScore: null,
    date: 'Sat 26 Jul',
    time: '17:30',
    venue: 'Emirates Stadium',
    status: 'upcoming',
    img: '/assets/images/sports-schedule-card.png',
  },
  {
    id: 'm2',
    league: 'KPL',
    leagueBadge: '🇰🇪',
    homeTeam: 'Gor Mahia',
    awayTeam: 'AFC Leopards',
    homeScore: 2,
    awayScore: 1,
    date: 'Today',
    time: 'FT',
    venue: 'Nyayo Stadium, Nairobi',
    status: 'finished',
    img: '/assets/images/sports-team-photo.png',
  },
  {
    id: 'm3',
    league: 'La Liga',
    leagueBadge: '🇪🇸',
    homeTeam: 'Real Madrid',
    awayTeam: 'Barcelona',
    homeScore: null,
    awayScore: null,
    date: 'Sun 27 Jul',
    time: '20:00',
    venue: 'Santiago Bernabéu',
    status: 'upcoming',
    img: '/assets/images/sports-hero.png',
  },
  {
    id: 'm4',
    league: 'NBA',
    leagueBadge: '🇺🇸',
    homeTeam: 'Lakers',
    awayTeam: 'Warriors',
    homeScore: 108,
    awayScore: 112,
    date: 'Yesterday',
    time: 'FT',
    venue: 'Crypto.com Arena',
    status: 'finished',
    img: '/assets/images/sports-schedule-card.png',
  },
];

const featuredLeagues = [
  { id: 'l1', name: 'Premier League', country: 'England', teams: 20, img: '/assets/images/sports-hero.png', badge: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { id: 'l2', name: 'KPL', country: 'Kenya', teams: 18, img: '/assets/images/sports-team-photo.png', badge: '🇰🇪' },
  { id: 'l3', name: 'La Liga', country: 'Spain', teams: 20, img: '/assets/images/sports-schedule-card.png', badge: '🇪🇸' },
  { id: 'l4', name: 'Champions League', country: 'Europe', teams: 32, img: '/assets/images/sports-hero.png', badge: '⭐' },
];

export default function SportsPage() {
  const [notifyForm, setNotifyForm] = useState({ name: '', phone: '', email: '', sport: '', team: '' });
  const [submitted, setSubmitted] = useState(false);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'schedule' | 'leagues' | 'notify'>('schedule');

  const features = [
    { icon: Trophy, label: 'Live Scores', desc: 'Real-time match scores and updates' },
    { icon: Calendar, label: 'Match Schedules', desc: 'Upcoming fixtures and match times' },
    { icon: Users, label: 'Team Profiles', desc: 'Detailed team and player information' },
    { icon: Star, label: 'League Tables', desc: 'Current standings and statistics' },
    { icon: Target, label: 'Sports Predictions', desc: 'Community match predictions and polls' },
    { icon: Globe, label: 'Local & International', desc: 'Kenyan and international sports coverage' },
    { icon: Play, label: 'Sports Videos', desc: 'Highlights, interviews and analysis' },
    { icon: Bell, label: 'Match Reminders', desc: 'Never miss a game with smart alerts' },
  ];

  const sports = ['Football', 'Basketball', 'Rugby', 'Athletics', 'Boxing', 'Volleyball', 'Cricket', 'Formula Racing'];

  const handleSubmit = () => {
    if (notifyForm.name && notifyForm.phone) {
      setSubmitted(true);
    }
  };

  const toggleNotification = (type: string) => {
    setNotifications(prev =>
      prev.includes(type) ? prev.filter(n => n !== type) : [...prev, type]
    );
  };

  return (
    <AppLayout>
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-0">
        {/* Hero with realistic sports photography */}
        <div className="relative rounded-3xl overflow-hidden mb-6 mt-4" style={{ minHeight: 300 }}>
          <img
            src="/assets/images/sports-hero.png"
            alt="Sports stadium with crowd cheering at a football match"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.2) 100%)' }} />
          <div className="relative z-10 p-6 flex flex-col justify-end h-full" style={{ minHeight: 300 }}>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-800 text-white" style={{ background: 'rgba(255,59,92,0.85)', backdropFilter: 'blur(4px)' }}>
                COMING SOON
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-700 text-white" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
                🏆 Sports App
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-700" style={{ background: 'rgba(0,200,83,0.2)', color: '#00C853', border: '1px solid rgba(0,200,83,0.4)' }}>
                <Zap size={10} className="inline mr-1" />Preview Mode
              </span>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">China Ndogo Sports</h1>
            <p className="text-sm text-white/80 max-w-md mb-4">Your complete sports companion for Kenyan and international sports. Live scores, match schedules, team profiles and more.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('notify')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-700 text-white transition-all active:scale-95"
                style={{ background: 'linear-gradient(135deg, #c8102e, #ff1744)', boxShadow: '0 4px 16px rgba(200,16,46,0.4)' }}
              >
                <Bell size={14} /> Notify Me
              </button>
              <button
                onClick={() => setActiveTab('schedule')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-700 transition-all active:scale-95"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', backdropFilter: 'blur(4px)' }}
              >
                <Calendar size={14} /> Preview Schedule
              </button>
            </div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide">
          {[
            { id: 'schedule', label: '📅 Schedule Preview' },
            { id: 'leagues', label: '🏆 Leagues' },
            { id: 'notify', label: '🔔 Get Notified' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-600 transition-all duration-150"
              style={{
                background: activeTab === tab.id ? 'rgba(0,200,83,0.15)' : 'var(--secondary)',
                color: activeTab === tab.id ? 'var(--primary)' : 'var(--muted-foreground)',
                border: `1px solid ${activeTab === tab.id ? 'var(--primary)' : 'var(--border)'}`,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Schedule Preview Tab */}
        {activeTab === 'schedule' && (
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-800 text-foreground">Upcoming & Recent Matches</h2>
              <span className="text-xs font-600 px-3 py-1 rounded-full" style={{ background: 'rgba(255,179,0,0.1)', color: 'var(--accent)', border: '1px solid rgba(255,179,0,0.2)' }}>
                Preview Only
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {scheduleMatches.map(match => (
                <div
                  key={match.id}
                  className="relative overflow-hidden rounded-2xl"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {/* Background image strip */}
                  <div className="absolute inset-0 opacity-10">
                    <img src={match.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="relative z-10 p-4">
                    {/* League header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{match.leagueBadge}</span>
                        <span className="text-xs font-700" style={{ color: 'var(--muted-foreground)' }}>{match.league}</span>
                      </div>
                      <span
                        className="text-[10px] font-800 px-2 py-0.5 rounded-full"
                        style={{
                          background: match.status === 'finished' ? 'rgba(0,200,83,0.15)' : 'rgba(255,179,0,0.15)',
                          color: match.status === 'finished' ? '#00C853' : '#FFB300',
                        }}
                      >
                        {match.status === 'finished' ? 'FINISHED' : 'UPCOMING'}
                      </span>
                    </div>

                    {/* Teams and score */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1 text-center">
                        <div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center text-lg font-800"
                          style={{ background: 'var(--secondary)', border: '2px solid var(--border)' }}>
                          {match.homeTeam.charAt(0)}
                        </div>
                        <p className="text-sm font-700 text-foreground">{match.homeTeam}</p>
                      </div>
                      <div className="px-4 text-center">
                        {match.status === 'finished' ? (
                          <div className="text-2xl font-900 text-foreground">
                            {match.homeScore} <span style={{ color: 'var(--muted-foreground)' }}>-</span> {match.awayScore}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-1 text-xs font-700" style={{ color: 'var(--accent)' }}>
                              <Clock size={10} /> {match.time}
                            </div>
                            <div className="text-lg font-800 text-foreground">VS</div>
                            <div className="text-xs font-600" style={{ color: 'var(--muted-foreground)' }}>{match.date}</div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-center">
                        <div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center text-lg font-800"
                          style={{ background: 'var(--secondary)', border: '2px solid var(--border)' }}>
                          {match.awayTeam.charAt(0)}
                        </div>
                        <p className="text-sm font-700 text-foreground">{match.awayTeam}</p>
                      </div>
                    </div>

                    {/* Venue */}
                    <div className="flex items-center gap-1.5 justify-center">
                      <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{match.venue}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leagues Tab */}
        {activeTab === 'leagues' && (
          <div className="flex flex-col gap-4 mb-6">
            <h2 className="text-lg font-800 text-foreground">Featured Leagues & Competitions</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {featuredLeagues.map(league => (
                <div
                  key={league.id}
                  className="relative overflow-hidden rounded-2xl cursor-pointer transition-all active:scale-95"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)', minHeight: 140 }}
                >
                  <img src={league.img} alt={league.name} className="absolute inset-0 w-full h-full object-cover opacity-30" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 60%)' }} />
                  <div className="relative z-10 p-3 flex flex-col justify-end h-full" style={{ minHeight: 140 }}>
                    <div className="text-2xl mb-1">{league.badge}</div>
                    <p className="text-sm font-800 text-white">{league.name}</p>
                    <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{league.country} · {league.teams} teams</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Features grid */}
            <h2 className="text-lg font-800 text-foreground mt-2">What's Coming</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {features.map((feature) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={feature.label} className="p-4 rounded-2xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: 'rgba(0,200,83,0.1)' }}>
                      <FeatureIcon size={18} style={{ color: 'var(--primary)' }} />
                    </div>
                    <p className="text-sm font-700 text-foreground mb-0.5">{feature.label}</p>
                    <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notify Tab */}
        {activeTab === 'notify' && (
          <div className="mb-6">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,200,83,0.15)', border: '2px solid var(--primary)' }}>
                  <Check size={28} style={{ color: 'var(--primary)' }} />
                </div>
                <h3 className="text-xl font-800 text-foreground">You're on the list!</h3>
                <p className="text-sm text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
                  We'll notify you when China Ndogo Sports launches. Get ready for live scores, match schedules and more!
                </p>
                <button
                  onClick={() => { setSubmitted(false); setNotifyForm({ name: '', phone: '', email: '', sport: '', team: '' }); }}
                  className="px-4 py-2 rounded-xl text-sm font-600 transition-all"
                  style={{ background: 'var(--secondary)', color: 'var(--foreground)', border: '1px solid var(--border)' }}
                >
                  Register Another
                </button>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <h2 className="text-lg font-800 text-foreground mb-1">Get Notified at Launch</h2>
                <p className="text-sm mb-5" style={{ color: 'var(--muted-foreground)' }}>Be the first to know when China Ndogo Sports goes live.</p>
                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    placeholder="Your name *"
                    value={notifyForm.name}
                    onChange={e => setNotifyForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone number *"
                    value={notifyForm.phone}
                    onChange={e => setNotifyForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                  <input
                    type="email"
                    placeholder="Email address (optional)"
                    value={notifyForm.email}
                    onChange={e => setNotifyForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                  <div>
                    <p className="text-xs font-600 mb-2" style={{ color: 'var(--muted-foreground)' }}>Favourite Sport</p>
                    <div className="flex flex-wrap gap-2">
                      {sports.map(sport => (
                        <button
                          key={sport}
                          onClick={() => setNotifyForm(p => ({ ...p, sport }))}
                          className="px-3 py-1.5 rounded-full text-xs font-600 transition-all"
                          style={{
                            background: notifyForm.sport === sport ? 'rgba(0,200,83,0.15)' : 'var(--secondary)',
                            color: notifyForm.sport === sport ? 'var(--primary)' : 'var(--muted-foreground)',
                            border: `1px solid ${notifyForm.sport === sport ? 'var(--primary)' : 'var(--border)'}`,
                          }}
                        >
                          {sport}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Favourite team (optional)"
                    value={notifyForm.team}
                    onChange={e => setNotifyForm(p => ({ ...p, team: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                  <div>
                    <p className="text-xs font-600 mb-2" style={{ color: 'var(--muted-foreground)' }}>Notification Preferences</p>
                    <div className="flex flex-col gap-2">
                      {['Match reminders', 'Live score alerts', 'Team news', 'Launch announcement'].map(type => (
                        <label key={type} className="flex items-center gap-3 cursor-pointer">
                          <div
                            onClick={() => toggleNotification(type)}
                            className="w-5 h-5 rounded-md flex items-center justify-center transition-all"
                            style={{
                              background: notifications.includes(type) ? 'var(--primary)' : 'var(--secondary)',
                              border: `2px solid ${notifications.includes(type) ? 'var(--primary)' : 'var(--border)'}`,
                            }}
                          >
                            {notifications.includes(type) && <Check size={11} color="#fff" />}
                          </div>
                          <span className="text-sm text-foreground">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={!notifyForm.name || !notifyForm.phone}
                    className="w-full py-3 rounded-xl text-sm font-700 text-white transition-all active:scale-95 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #c8102e, #ff1744)', boxShadow: '0 4px 16px rgba(200,16,46,0.3)' }}
                  >
                    <Bell size={14} className="inline mr-2" />
                    Notify Me at Launch
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
